import { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { SButton, SDropdownMenu } from '@setvi/shared/components';
import {
  PlusIcon,
  AddBySKUIcon,
  AddSPOIcon,
  AddSectionIcon
} from '@setvi/shared/components/sicons';
import { useDialog } from '@setvi/shared/providers';
import { FillCatalogIcon } from 'pages/quotes/icons';
import {
  useQuoteData,
  useQuoteMutations,
  useQuoteStatusFlags
} from '../../../context';
import { AddFromCatalogDialog } from '../../add-from-catalog-dialog';
import { AddBySku } from '../../add-by-sku';
import { AddSPO } from '../../add-spo';
import { SectionDialog } from '../../add-section-dialog';

const ACCENT = '#1E96FC';

const useStyles = makeStyles(() => ({
  // Tall wrapper (overflows the gap) so the Add button renders in full, but it
  // never blocks the neighbouring rows: the wrapper itself is pointer-inert and
  // only the thin `hotzone` inside the gap listens for hover.
  strip: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -30,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 5,
    '&:hover $content': {
      opacity: 1,
      pointerEvents: 'auto'
    }
  },
  // Keep the line + button shown while the dropdown is open (the pointer has
  // moved off the strip into the menu portal, so :hover no longer applies).
  open: {
    '& $content': {
      opacity: 1,
      pointerEvents: 'auto'
    }
  },
  // Hover trigger confined to the 12px gap between rows (centered on the strip),
  // so hovering it never overlaps a row.
  hotzone: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    height: 12,
    marginTop: -6,
    pointerEvents: 'auto'
  },
  // Line + button reveal on hover. Kept pointer-inert while hidden so the
  // (taller) button can overflow the gap without blocking rows.
  content: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.15s ease'
  },
  line: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    height: 2,
    marginTop: -1,
    backgroundColor: ACCENT,
    borderRadius: 1
  },
  button: {
    position: 'relative'
  },
  // Shrink the dropdown trigger to the button so the menu anchors to the
  // button (not the full-width strip) and opens centered directly below it.
  trigger: {
    display: 'inline-flex',
    position: 'relative'
  }
}));

interface InsertDividerProps {
  /** Item to insert the new product directly after. */
  afterItemId: string;
  /**
   * Whether the "Add New Section" option is offered. Only true at root level —
   * sections cannot be nested inside another section.
   */
  allowSection?: boolean;
}

/**
 * Hover affordance shown in the gap below a line item. Reveals an "Add" button
 * whose dropdown inserts the chosen product (or section) directly after
 * `afterItemId` rather than appending it (see useAddProducts / useAddFreehand /
 * useAddSection).
 */
export const InsertDivider = ({
  afterItemId,
  allowSection
}: InsertDividerProps) => {
  const classes = useStyles();
  const { openDialog, closeDialog } = useDialog();
  const { quote, currentStatus } = useQuoteData();
  const { addProducts, addFreehand, addSection } = useQuoteMutations();
  const { isAddingProducts, isAddingFreehand } = useQuoteStatusFlags();

  const [menuOpen, setMenuOpen] = useState(false);
  const stripRef = useRef<HTMLDivElement>(null);

  // Each row wrapper has a transform (its own stacking context), so the strip's
  // z-index can't lift the button above the next row on its own. Hovering the
  // row bumps its z-index via CSS, but once the dropdown is open the pointer
  // moves into the menu portal and that :hover is lost — so while the menu is
  // open we elevate the enclosing row wrapper directly to keep the button on top.
  useEffect(() => {
    const wrapper = stripRef.current?.closest(
      '[data-index]'
    ) as HTMLElement | null;
    if (!wrapper || !menuOpen) return undefined;

    wrapper.style.zIndex = '9';
    return () => {
      wrapper.style.zIndex = '';
    };
  }, [menuOpen]);

  const isQuoteEditable =
    !!currentStatus?.isQuoteEditable && !!quote?.acl?.canEditQuote;

  if (!isQuoteEditable) return null;

  const handleAddFromCatalog = () => {
    const id = `insert-catalog-${afterItemId}`;
    openDialog(
      <AddFromCatalogDialog
        onClose={() => closeDialog(id)}
        quote={quote as any}
        addProducts={({ products }) => addProducts({ products, afterItemId })}
      />,
      { id }
    );
  };

  const handleAddBySku = () => {
    const id = `insert-by-sku-${afterItemId}`;
    openDialog(
      <AddBySku
        onClose={() => closeDialog(id)}
        addProducts={({ products }) => addProducts({ products, afterItemId })}
        isAddingProducts={isAddingProducts}
        selectedCustomer={quote?.account as any}
        segmentId={quote?.segment?._id}
      />,
      { id }
    );
  };

  const handleAddSpo = () => {
    const id = `insert-spo-${afterItemId}`;
    openDialog(
      <AddSPO
        onClose={() => closeDialog(id)}
        onSubmit={async spoData => {
          await addFreehand({ ...spoData, afterItemId });
          closeDialog(id);
        }}
        isAddingProducts={isAddingFreehand}
      />,
      { id }
    );
  };

  const handleAddSection = () => {
    const id = `insert-section-${afterItemId}`;
    openDialog(
      <SectionDialog
        onClose={() => closeDialog(id)}
        addSection={params => {
          addSection({ ...params, afterItemId });
          closeDialog(id);
        }}
      />,
      { id }
    );
  };

  const menuItems = [
    {
      icon: <FillCatalogIcon />,
      text: 'Add From Catalog',
      onClick: handleAddFromCatalog
    },
    {
      icon: <AddBySKUIcon />,
      text: 'Add by SKU',
      onClick: handleAddBySku
    },
    {
      icon: <AddSPOIcon />,
      text: 'Add SPO',
      onClick: handleAddSpo,
      divider: allowSection
    },
    ...(allowSection
      ? [
          {
            icon: <AddSectionIcon />,
            text: 'Add New Section',
            onClick: handleAddSection
          }
        ]
      : [])
  ];

  return (
    <div
      ref={stripRef}
      className={`${classes.strip} ${menuOpen ? classes.open : ''}`}>
      <span className={classes.hotzone} />
      <div className={classes.content}>
        <span className={classes.line} />
        <span className={classes.trigger}>
          <SDropdownMenu
            newDesign
            menuItems={menuItems}
            onClose={() => setMenuOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}>
            <SButton
              className={classes.button}
              variant="contained"
              color="primary"
              onClick={() => setMenuOpen(true)}
              endIcon={<KeyboardArrowDownIcon htmlColor="#fff" />}
              startIcon={
                <PlusIcon
                  htmlColor="#fff"
                  style={{ fontSize: '13px', marginBottom: '2px' }}
                />
              }>
              Add
            </SButton>
          </SDropdownMenu>
        </span>
      </div>
    </div>
  );
};
